import React from 'react';
import { cn } from '@utils/cn';
import { InvoiceStatusBadge } from '@components/atoms/InvoiceStatusBadge';
import { Invoice } from '@models/invoice.model';

interface PaidListProps {
  invoices: Invoice[];
  onOpenInvoice: (invoiceId: string) => void;
  className?: string;
}

const formatCurrency = (value: number): string => {
  return `$ ${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const formatDate = (iso?: string): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

export const PaidList: React.FC<PaidListProps> = ({ invoices, onOpenInvoice, className = '' }) => {
  if (invoices.length === 0) {
    return (
      <div className={cn('flex-1 flex items-center justify-center px-6 py-10', className)}>
        <p className="text-sm text-gray-500">No invoices paid yet for this project.</p>
      </div>
    );
  }

  return (
    <div className={cn('flex-1 overflow-y-auto', className)}>
      <table className="w-full text-sm">
        <thead className="bg-gray-50">
          <tr className="text-xs text-gray-500 uppercase tracking-wide">
            <th className="text-left px-4 py-2.5">Invoice #</th>
            <th className="text-left px-4 py-2.5">Recipient</th>
            <th className="text-left px-4 py-2.5">Paid on</th>
            <th className="text-left px-4 py-2.5">NMI Tx</th>
            <th className="text-right px-4 py-2.5">Total</th>
            <th className="text-left px-4 py-2.5">Status</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => (
            <tr
              key={invoice.invoiceId}
              onClick={() => onOpenInvoice(invoice.invoiceId)}
              className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
              <td className="px-4 py-3 font-medium text-gray-900">{invoice.invoiceNumber || '—'}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-600">{invoice.recipientEmail}</td>
              <td className="px-4 py-3 text-gray-600">{formatDate(invoice.paidAt)}</td>
              <td className="px-4 py-3 font-mono text-xs text-gray-500">{invoice.nmiTransactionId || '—'}</td>
              <td className="px-4 py-3 text-right font-medium">{formatCurrency(Number(invoice.total))}</td>
              <td className="px-4 py-3"><InvoiceStatusBadge status={invoice.status} size="sm" /></td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};
